import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token management
let authToken = localStorage.getItem('symptoconnect_token');

// Refresh-in-progress state — prevents concurrent refresh races
let isRefreshing = false;
let refreshSubscribers = [];

const drainRefreshQueue = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and extract data
api.interceptors.response.use(
  (response) => {
    // If backend sends standardized format {success, data, message}, extract data
    if (response.data && typeof response.data === 'object') {
      // Keep the full response structure for flexibility
      // Components can access response.data.data or response.data directly
      return response;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      if (isRefreshing) {
        // Another refresh is already in flight — queue this request
        return new Promise((resolve) => {
          refreshSubscribers.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.get('/auth/refresh');
        setAuthToken(data.token);
        drainRefreshQueue(data.token);
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return api(originalRequest);
      } catch {
        // Refresh failed — session is truly over
        setAuthToken(null);
        refreshSubscribers = [];
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Enhance error message with backend error details
    if (error.response?.data?.error) {
      error.message = error.response.data.error;
    } else if (error.response?.data?.message) {
      error.message = error.response.data.message;
    }

    return Promise.reject(error);
  }
);

// Helper function to set auth token
export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('symptoconnect_token', token);
  } else {
    localStorage.removeItem('symptoconnect_token');
  }
};

// Helper function to get auth token
export const getAuthToken = () => authToken;

// API endpoints

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  refresh: () => api.get('/auth/refresh'),
};

// Symptoms endpoints
export const symptomsAPI = {
  create: (data) => api.post('/symptoms', data),
  getAll: (params = {}) => api.get('/symptoms', { params }),
  getMine: (params = {}) => api.get('/symptoms/me', { params }),
  getById: (id) => api.get(`/symptoms/${id}`),
  updateStatus: (id, status) => api.patch(`/symptoms/${id}`, { status }),
  getDashboardStats: () => api.get('/symptoms/stats/dashboard'),
};

// Responses endpoints
export const responsesAPI = {
  create: (symptomId, data) => api.post(`/responses/${symptomId}`, data),
  getBySymptom: (symptomId) => api.get(`/responses/${symptomId}`),
  update: (symptomId, responseId, data) => api.put(`/responses/${symptomId}/${responseId}`, data),
  delete: (symptomId, responseId) => api.delete(`/responses/${symptomId}/${responseId}`),
};

// Patient replies endpoints
export const patientRepliesAPI = {
  create: (symptomId, data) => api.post(`/patient-replies/${symptomId}`, data),
};

// Chat endpoints
export const chatAPI = {
  // Get chat history for a specific room
  getHistory: (roomId) => api.get(`/chat/${roomId}`),
  
  // Get available chat rooms for a symptom
  getRooms: (symptomId) => api.get(`/chat/symptom/${symptomId}/rooms`),
};

// Error handler helper
export const handleApiError = (error) => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default api;